import { Vector3 } from "three";
import { useEffect, useState } from "react";
import PrototypeImpl from "../PrototypeImpl.ts";
import { useModels } from "../Resources/ModelProvider.tsx";
import ErrorTile from "./ErrorTile.tsx";
import { useFrame } from "@react-three/fiber";
import { folder, useControls } from "leva";
import { animated, useSpring } from "@react-spring/three";

type TileProps = {
  tilePosition: Vector3;
  tileSize: number;
  prototype: PrototypeImpl;
};

export default function Tile(props: TileProps): JSX.Element {
  const model = useModels().models.get(props.prototype.mesh_name);

  const { enableRotation, enableAxesHelper } = useControls("Tile", {
    enableRotation: false,
    enableAxesHelper: false,
  });

  // Position of the tile (assigned to the "group")
  let position = props.tilePosition as Vector3;
  position.y -= props.tileSize / 2;

  // Center the model inside so that the center of rotation is the center of the tile
  const positionWithinGroup = new Vector3(0, props.tileSize / 2, 0);

  const [rotation, setRotation] = useState(0);
  useFrame(() => {
    if (enableRotation) {
      setRotation((prev) => prev + 0.01);
    } else if (rotation !== 0) {
      setRotation(0);
    }
  });

  const [spawned, setSpawned] = useState(false);

  useEffect(() => {
    setSpawned(props.prototype.mesh_name !== "-1");
  }, [model]);

  const [{ scaleY }] = useSpring(
    {
      scaleY: spawned ? [1] : [0.8],
      config: {
        mass: 4,
        friction: 30,
        tension: 700,
      },
    },
    [spawned],
  );

  const [{ scaleXZ }] = useSpring(
    {
      scaleXZ: spawned ? [1] : [0.8],
      config: {
        mass: 2,
        friction: 35,
        tension: 1000,
      },
    },
    [spawned],
  );

  const spawn = () => {
    console.log("Spawning");
    setSpawned(true);
  };

  const despawn = () => {
    console.log("Despawning");
    setSpawned(false);
  };

  useEffect(() => {
    if (props.prototype.mesh_name !== "-1" && model) {
      spawn();
    }
  }, []);

  return model ? (
    <animated.group
      scale-x={scaleXZ}
      scale-z={scaleXZ}
      scale-y={scaleY}
      position={position}
      rotation={[rotation, (Math.PI / 2) * props.prototype.mesh_rotation, 0]}
    >
      {enableAxesHelper && <axesHelper args={[1]} />}
      <primitive
        position={positionWithinGroup}
        object={model.clone()}
        key={props.prototype.mesh_name + props.tilePosition}
        scale={[2, 2, 2]}
        onClick={() =>
          console.log(
            "clicked collapsed tile",
            props.tilePosition,
            props.prototype,
          )
        }
      />
    </animated.group>
  ) : props.prototype.mesh_name === "-1" ? (
    <></> // Empty tile
  ) : (
    <ErrorTile tilePosition={props.tilePosition} prototype={props.prototype} />
  );
}
